import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Users, FileEdit, ChevronDown } from "lucide-react";
import {
  HERO_BADGE_TEXT, COURSE_SEATS,
} from "./kursusConstants";

const HERO_IMG = "https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&q=85";

const EASE = [0.22, 1, 0.36, 1];

const scrollTo = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth", block: "start" });

export function KursusHero() {
  return (
    <section data-testid="kursus-hero" className="relative min-h-[92vh] sm:min-h-[85vh] flex items-end sm:items-center overflow-hidden bg-kursus-bg">
      <div className="absolute inset-0 overflow-hidden">
        <img src={HERO_IMG} alt="Latihan Pengawal Peribadi Bodyguard Sarawak" className="kursus-hero-zoom w-full h-full object-cover object-top opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-kursus-bg via-kursus-bg/70 to-kursus-bg/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-kursus-bg via-kursus-bg/50 to-transparent" />
      </div>
      <div className="kursus-tactical-grid" />
      <div className="kursus-scanline" />

      <div className="hidden sm:block absolute top-6 left-6 w-14 h-14 border-t-2 border-l-2 border-kursus-gold/60" />
      <div className="hidden sm:block absolute top-6 right-6 w-14 h-14 border-t-2 border-r-2 border-kursus-gold/60" />
      <div className="hidden sm:block absolute bottom-6 left-6 w-14 h-14 border-b-2 border-l-2 border-kursus-gold/30" />

      <div className="absolute top-0 right-[8%] h-full w-[3px] bg-kursus-red/70 hidden lg:block" style={{ transform: "skewX(-8deg)" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-32 sm:pt-0 w-full">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            data-testid="kursus-hero-badge"
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-kursus-gold/50 bg-kursus-gold/10 text-kursus-gold font-oswald text-xs sm:text-sm font-semibold tracking-[0.2em] mb-6"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-kursus-red" /> {HERO_BADGE_TEXT}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
            className="font-anton uppercase leading-[0.95] text-[2.75rem] sm:text-6xl lg:text-7xl text-kursus-white"
            data-testid="kursus-hero-title"
          >
            KURSUS ASAS<br />
            <span className="text-kursus-gold" style={{ textShadow: "0 2px 24px rgba(217,154,24,0.35)" }}>PENGAWAL PERIBADI</span>
          </motion.h1>

          <motion.div
            initial={{ width: 0 }}
            animate={{ width: 120 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.55 }}
            className="h-[3px] bg-gradient-to-r from-kursus-gold to-kursus-goldbright mt-5 mb-6"
          />

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE, delay: 0.65 }}
            className="font-oswald uppercase tracking-wide text-sm sm:text-base text-kursus-white/90 leading-relaxed mb-8"
            data-testid="kursus-hero-subhead"
          >
            Bina Disiplin. <span className="text-kursus-muted">Tingkatkan Kesiapsiagaan.</span><br className="hidden sm:block" />
            Fahami Tanggungjawab Perlindungan.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE, delay: 0.8 }}
            className="mb-9"
            data-testid="kursus-hero-info-row"
          >
            <div className="inline-flex items-start gap-2 border-l-2 border-kursus-gold/40 pl-2.5">
              <Users className="w-4 h-4 text-kursus-gold shrink-0 mt-0.5" />
              <span className="font-oswald text-[11px] sm:text-xs text-kursus-white/85 uppercase leading-tight">{COURSE_SEATS} PESERTA SAHAJA</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE, delay: 0.95 }}
            className="flex flex-col sm:flex-row gap-3.5"
          >
            <Button
              data-testid="kursus-hero-cta-daftar"
              onClick={() => scrollTo("#daftar")}
              className="h-auto px-6 py-3.5 bg-kursus-gold hover:bg-kursus-goldbright text-black font-oswald font-bold tracking-wide rounded-md text-sm sm:text-base gap-2 shadow-[0_0_30px_rgba(217,154,24,0.25)]"
            >
              <FileEdit className="w-4 h-4" /> DAFTAR SEKARANG
            </Button>
            <Button
              data-testid="kursus-hero-cta-details"
              variant="outline"
              onClick={() => scrollTo("#butiran")}
              className="h-auto px-6 py-3.5 border-kursus-white/30 text-kursus-white hover:bg-kursus-white/10 hover:text-white bg-transparent font-oswald font-bold tracking-wide rounded-md text-sm sm:text-base gap-2"
            >
              LIHAT BUTIRAN KURSUS <ChevronDown className="w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
